import { Controller } from "@hotwired/stimulus"

// Team cards on /about: click a card to flip it to a short bio, then the
// button in the bottom-right corner opens the full bio in a modal.
//
// Progressive enhancement. Without this controller the cards render as plain
// photo + name + role, exactly as they did before — connect() adds
// `dfab-team--ready`, which is what reveals the flip affordance and the
// bottom-right expand button. So a JS failure degrades to a static card rather
// than to buttons that do nothing.
//
// The back face is `inert` while it is turned away. backface-visibility only
// hides it visually: without inert, tabbing would move focus onto an invisible
// bio and its expand button.
export default class extends Controller {
  static targets = ["card", "front", "back", "dialog"]

  connect() {
    this.element.classList.add("dfab-team--ready")
    this.cardTargets.forEach((card) => this.#sync(card))

    // Clicking the backdrop closes. <dialog> reports backdrop clicks as clicks
    // on the dialog itself, so an event whose target IS the dialog element
    // landed outside the content panel. Esc is handled by the element.
    this.dialogTargets.forEach((dialog) => {
      dialog.addEventListener("click", (event) => {
        if (event.target === dialog) dialog.close()
      })
    })

    // Arriving from a blog byline (/about#team-selcuk-kubur) should land on that
    // person's card already turned to their bio — otherwise the link drops you
    // at a photo and the bio you were promised is a click away.
    this.#openFromHash()
    this.hashListener = () => this.#openFromHash()
    window.addEventListener("hashchange", this.hashListener)
  }

  disconnect() {
    window.removeEventListener("hashchange", this.hashListener)
  }

  flip(event) {
    const card = event.currentTarget.closest("[data-about-team-target~='card']")
    if (!card) return

    card.classList.toggle("is-flipped")
    this.#sync(card)

    // Move focus to the face now showing, so a keyboard user is not left on a
    // control that just rotated out of view.
    const face = card.classList.contains("is-flipped") ? "back" : "front"
    card.querySelector(`.dfab-face--${face} .dfab-flipbtn`)?.focus()
  }

  open(event) {
    // Stop the click reaching the flip button underneath, which would spin the
    // card back to its photo behind the modal.
    event.stopPropagation()
    document.getElementById(event.currentTarget.dataset.aboutTeamDialog)?.showModal()
  }

  close(event) {
    event.currentTarget.closest("dialog")?.close()
  }

  // Turn the card named in the URL hash face-up and bring it into view.
  //
  // The browser has already jumped to the element by id, so this is only about
  // the flip and about re-centring: the card is tall, and the native jump puts
  // its top edge at the very top of the viewport.
  #openFromHash() {
    const id = window.location.hash.slice(1)
    if (!id) return

    const card = this.cardTargets.find((c) => c.id === id)
    if (!card) return

    if (!card.classList.contains("is-flipped")) {
      card.classList.add("is-flipped")
      this.#sync(card)
    }

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    card.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "center" })
    card.classList.add("is-linked")
  }

  #sync(card) {
    const flipped = card.classList.contains("is-flipped")
    const front = card.querySelector(".dfab-face--front")
    const back = card.querySelector(".dfab-face--back")

    if (front) front.inert = flipped
    if (back) back.inert = !flipped
    card.querySelector(".dfab-face--front .dfab-flipbtn")?.setAttribute("aria-expanded", String(flipped))
  }
}
